// Property editors for the app-integration blocks that had none — the
// `filter_bar` (search / facets / sort over another block's items) and the
// `auth_form` (a branded sign-in / sign-up form) — plus the draft-text field
// the page designer's list/line editors share.
//
// Every control shows the RUNTIME default as its placeholder (see
// page-runtime/renderers.tsx FilterBarRenderer / AuthFormRenderer), a blank
// input saves `undefined` rather than a value the author never chose, and a
// select never hides a saved value it does not recognise.

import { useEffect, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'

import { newId } from './blocks'
import { walkBlocks } from './pageOps'
import {
  FIELD_LABEL, INPUT, HINT, SUBSECTION, TWO_COL, EnumSelect, ItemControls, moveItem,
} from './blockEditors'
import type {
  AuthFormBlock, Block, FilterBarBlock, FilterFacet, FilterFacetSource, FilterSortOption, PageModel,
} from './types'

// ─── Draft text field ────────────────────────────────
// A comma- or line-separated list re-parsed on every keystroke eats input:
// the parse drops the empty item the author is about to type into, so Enter
// can never start a new line and a trailing comma vanishes. This keeps the
// raw text as a local draft, commits it on blur, and re-syncs from the saved
// value whenever that changes while the field is not being edited (another
// block selected, an undo, an agent edit).
export function DraftTextField({ value, onCommit, multiline, rows = 3, placeholder, style }: {
  value: string
  onCommit: (text: string) => void
  multiline?: boolean
  rows?: number
  placeholder?: string
  style?: React.CSSProperties
}) {
  const [draft, setDraft] = useState(value)
  const [editing, setEditing] = useState(false)
  useEffect(() => { if (!editing) setDraft(value) }, [value, editing])
  const commit = () => {
    setEditing(false)
    if (draft !== value) onCommit(draft)
  }
  const common = {
    value: draft,
    placeholder,
    onFocus: () => setEditing(true),
    onBlur: commit,
    style: { ...INPUT, ...style },
  }
  return multiline
    ? <textarea {...common} rows={rows} onChange={(e) => setDraft(e.target.value)} />
    : <input
        type="text" {...common}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
      />
}

// ─── Filter bar ──────────────────────────────────────

const FACET_SOURCES: ReadonlyArray<readonly [FilterFacetSource, string]> = [
  ['eyebrow', 'Card eyebrow (category line)'],
  ['badge', 'Card badge (ribbon)'],
  ['status', 'Card status'],
]
const SORT_BY: ReadonlyArray<readonly [FilterSortOption['by'], string]> = [
  ['price', 'Price'], ['title', 'Title'], ['none', 'Authored order'],
]
const SORT_DIR: ReadonlyArray<readonly ['asc' | 'desc', string]> = [
  ['asc', 'Ascending'], ['desc', 'Descending'],
]

/** Blocks a filter bar can filter: a `card_grid`, or any block repeated over
 *  a list data source. The bar matches its target by block id
 *  (`data-pg-block`), anywhere on the page. */
function filterTargets(page: PageModel): { id: string; label: string }[] {
  const out: { id: string; label: string }[] = []
  const visit = (b: Block) => {
    if (b.type === 'card_grid' || b.repeat?.source) {
      out.push({ id: b.id, label: `${b.id} (${b.type}${b.repeat?.source ? ` · repeats ${b.repeat.source}` : ''})` })
    }
  }
  for (const sec of page.sections ?? []) walkBlocks(sec.blocks ?? [], visit)
  walkBlocks(page.blocks ?? [], visit)
  return out
}

export function FilterBarEditor({ block, page, onChange }: {
  block: FilterBarBlock
  page: PageModel
  onChange: (b: Block) => void
}) {
  const upd = (patch: Partial<FilterBarBlock>) => onChange({ ...block, ...patch })
  const targets = useMemo(() => filterTargets(page), [page])
  const facets = block.facets || []
  const sorts = block.sort || []
  const targetKnown = !block.target || targets.some((t) => t.id === block.target)

  const updFacet = (i: number, patch: Partial<FilterFacet>) =>
    upd({ facets: facets.map((f, j) => (j === i ? { ...f, ...patch } : f)) })
  const setFacets = (next: FilterFacet[]) => upd({ facets: next.length ? next : undefined })
  const updSort = (i: number, patch: Partial<FilterSortOption>) =>
    upd({ sort: sorts.map((o, j) => (j === i ? { ...o, ...patch } : o)) })
  const setSorts = (next: FilterSortOption[]) => upd({ sort: next.length ? next : undefined })
  const updSearch = (patch: Partial<NonNullable<FilterBarBlock['search']>>) =>
    upd({ search: { ...(block.search || {}), ...patch } })

  return (
    <>
      <div>
        <label style={FIELD_LABEL}>Filters the items of</label>
        <select value={block.target || ''} onChange={(e) => upd({ target: e.target.value })} style={INPUT}>
          <option value="">— choose a block —</option>
          {!targetKnown && <option value={block.target}>{block.target} (not found)</option>}
          {targets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <p style={HINT}>
          A card grid, or any block repeated over a list data source.
          {targets.length === 0 && ' This page has none yet.'}
        </p>
      </div>

      <div>
        <label style={FIELD_LABEL}>Layout</label>
        <EnumSelect
          value={block.layout} fallback="sidebar"
          options={[['sidebar', 'Sidebar (stacked)'], ['inline', 'Inline (one row)']]}
          onChange={(layout) => upd({ layout })}
        />
      </div>

      <div style={SUBSECTION}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
          <input
            type="checkbox" checked={!!block.search}
            onChange={(e) => upd({ search: e.target.checked ? (block.search || {}) : undefined })}
          />
          Search box
        </label>
        {block.search && (
          <div style={TWO_COL}>
            <div>
              <label style={FIELD_LABEL}>Label</label>
              <input value={block.search.label ?? ''} placeholder="Search"
                onChange={(e) => updSearch({ label: e.target.value || undefined })} style={INPUT} />
            </div>
            <div>
              <label style={FIELD_LABEL}>Placeholder</label>
              <input value={block.search.placeholder ?? ''} placeholder="Search"
                onChange={(e) => updSearch({ placeholder: e.target.value || undefined })} style={INPUT} />
            </div>
          </div>
        )}
      </div>

      <div>
        <label style={FIELD_LABEL}>Facets</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {facets.map((f, i) => (
            <div key={f.id || i} style={SUBSECTION}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input value={f.label ?? ''} placeholder="Label"
                  onChange={(e) => updFacet(i, { label: e.target.value })} style={{ ...INPUT, flex: 1 }} />
                <ItemControls
                  onUp={() => setFacets(moveItem(facets, i, -1))}
                  onDown={() => setFacets(moveItem(facets, i, 1))}
                  onRemove={() => setFacets(facets.filter((_, j) => j !== i))}
                  canUp={i > 0} canDown={i < facets.length - 1}
                />
              </div>
              <div>
                <label style={FIELD_LABEL}>Values come from</label>
                <EnumSelect value={f.from} fallback="eyebrow" options={FACET_SOURCES}
                  onChange={(from) => updFacet(i, { from })} />
              </div>
              <div style={TWO_COL}>
                <div>
                  <label style={FIELD_LABEL}>"All" label</label>
                  <input value={f.allLabel ?? ''} placeholder="All"
                    onChange={(e) => updFacet(i, { allLabel: e.target.value || undefined })} style={INPUT} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer', alignSelf: 'end', paddingBottom: 6 }}>
                  <input type="checkbox" checked={f.showCounts !== false}
                    onChange={(e) => updFacet(i, { showCounts: e.target.checked })} />
                  Show counts
                </label>
              </div>
            </div>
          ))}
          <button
            onClick={(e) => { e.stopPropagation(); setFacets([...facets, { id: newId('fc'), label: 'Category', from: 'eyebrow' }]) }}
            className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
          ><Plus size={13} /> Add facet</button>
        </div>
        <p style={HINT}>The choices are read off the items themselves, so there is no value list to keep.</p>
      </div>

      <div>
        <label style={FIELD_LABEL}>Sort options</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sorts.map((o, i) => (
            <div key={o.id || i} style={SUBSECTION}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input value={o.label ?? ''} placeholder="Label"
                  onChange={(e) => updSort(i, { label: e.target.value })} style={{ ...INPUT, flex: 1 }} />
                <ItemControls
                  onUp={() => setSorts(moveItem(sorts, i, -1))}
                  onDown={() => setSorts(moveItem(sorts, i, 1))}
                  onRemove={() => setSorts(sorts.filter((_, j) => j !== i))}
                  canUp={i > 0} canDown={i < sorts.length - 1}
                />
              </div>
              <div style={TWO_COL}>
                <EnumSelect value={o.by} fallback="price" options={SORT_BY} onChange={(by) => updSort(i, { by })} />
                <EnumSelect value={o.dir} fallback="asc" options={SORT_DIR} onChange={(dir) => updSort(i, { dir })} />
              </div>
            </div>
          ))}
          <button
            onClick={(e) => { e.stopPropagation(); setSorts([...sorts, { id: newId('so'), label: 'Price: low to high', by: 'price', dir: 'asc' }]) }}
            className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
          ><Plus size={13} /> Add sort option</button>
        </div>
      </div>

      <div>
        <label style={FIELD_LABEL}>Result count label</label>
        <input value={block.resultLabel ?? ''} placeholder="Showing {n}"
          onChange={(e) => upd({ resultLabel: e.target.value || undefined })} style={INPUT} />
        <p style={HINT}><code>{'{n}'}</code> is replaced with how many items are showing. Blank hides the count.</p>
      </div>
    </>
  )
}

// ─── Auth form ───────────────────────────────────────

export function AuthFormEditor({ block, onChange }: {
  block: AuthFormBlock
  page: PageModel
  onChange: (b: Block) => void
}) {
  const upd = (patch: Partial<AuthFormBlock>) => onChange({ ...block, ...patch })
  const register = block.mode === 'register'
  const identify = block.mode === 'identify'
  const defaultTitle = identify ? 'Your details' : register ? 'Create account' : 'Sign in'
  return (
    <>
      <div>
        <label style={FIELD_LABEL}>Starts as</label>
        <EnumSelect
          value={block.mode} fallback="login"
          options={[['login', 'Sign in'], ['register', 'Sign up (create account)'],
            ['identify', 'Email or mobile first (log in, or create the account on the spot)']]}
          onChange={(mode) => upd({ mode })}
        />
        {identify && (
          <p style={{ ...HINT, margin: '4px 0 0' }}>
            The visitor types an email or mobile number. A known one asks for the password
            (with "Forgot password?"); an unknown one creates the account, signs them in and
            sends a set-password link — only when the project allows self-registration.
          </p>
        )}
      </div>
      <div>
        <label style={FIELD_LABEL}>Identifier field label</label>
        <input value={block.identifierLabel ?? ''} placeholder="Email or mobile number"
          onChange={(e) => upd({ identifierLabel: e.target.value || undefined })} style={INPUT} />
      </div>
      <div>
        <label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading ?? ''} placeholder={defaultTitle}
          onChange={(e) => upd({ heading: e.target.value || undefined })} style={INPUT} />
      </div>
      <div>
        <label style={FIELD_LABEL}>Subheading</label>
        <input value={block.subheading ?? ''}
          onChange={(e) => upd({ subheading: e.target.value || undefined })} style={INPUT} />
      </div>
      <div>
        <label style={FIELD_LABEL}>Submit button label</label>
        <input value={block.submitLabel ?? ''} placeholder={defaultTitle}
          onChange={(e) => upd({ submitLabel: e.target.value || undefined })} style={INPUT} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={block.showTwasta !== false}
          onChange={(e) => upd({ showTwasta: e.target.checked })} />
        Offer "Sign in with Twasta" (sign-in mode)
      </label>
      <div style={SUBSECTION}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={block.showRegisterLink !== false}
            onChange={(e) => upd({ showRegisterLink: e.target.checked })} />
          Link to switch between sign-in and sign-up
        </label>
        {block.showRegisterLink !== false && (
          <div>
            <label style={FIELD_LABEL}>Link label</label>
            <input value={block.registerLinkLabel ?? ''}
              placeholder={register ? 'Already have an account? Sign in' : 'Create an account'}
              onChange={(e) => upd({ registerLinkLabel: e.target.value || undefined })} style={INPUT} />
          </div>
        )}
        <p style={{ ...HINT, margin: 0 }}>Hidden automatically when the project does not allow self-registration.</p>
      </div>
      <div>
        <label style={FIELD_LABEL}>"Forgot password?" link</label>
        <input value={block.forgotHref ?? ''} placeholder="/forgot-password"
          onChange={(e) => upd({ forgotHref: e.target.value || undefined })} style={{ ...INPUT, fontFamily: 'monospace' }} />
        <p style={HINT}>Shown in sign-in mode only. Blank hides the link.</p>
      </div>
      <div style={SUBSECTION}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!block.tabs}
            onChange={(e) => upd({ tabs: e.target.checked || undefined })} />
          "Sign in" / "Create account" tabs
        </label>
        <p style={{ ...HINT, margin: 0 }}>
          One card, two tabs. The heading above belongs to the sign-in tab; a link ending
          <code> ?mode=register</code> opens the create-account tab.
        </p>
        {block.tabs && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <div>
                <label style={FIELD_LABEL}>Sign-in tab</label>
                <input value={block.signInTabLabel ?? ''} placeholder="Sign in"
                  onChange={(e) => upd({ signInTabLabel: e.target.value || undefined })} style={INPUT} />
              </div>
              <div>
                <label style={FIELD_LABEL}>Create tab</label>
                <input value={block.registerTabLabel ?? ''} placeholder="Create account"
                  onChange={(e) => upd({ registerTabLabel: e.target.value || undefined })} style={INPUT} />
              </div>
            </div>
            <div>
              <label style={FIELD_LABEL}>Note under the sign-in step</label>
              <input value={block.signInNote ?? ''}
                onChange={(e) => upd({ signInNote: e.target.value || undefined })} style={INPUT} />
            </div>
          </>
        )}
        {identify && (
          <div>
            <label style={FIELD_LABEL}>Identifier placeholder</label>
            <input value={block.identifierPlaceholder ?? ''} placeholder="Email address or mobile number"
              onChange={(e) => upd({ identifierPlaceholder: e.target.value || undefined })} style={INPUT} />
          </div>
        )}
      </div>
      {(block.tabs || register) && (
        <div style={SUBSECTION}>
          <label style={FIELD_LABEL}>Create-account form</label>
          <div>
            <label style={FIELD_LABEL}>Heading</label>
            <input value={block.registerHeading ?? ''} placeholder="Create account"
              onChange={(e) => upd({ registerHeading: e.target.value || undefined })} style={INPUT} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Subheading</label>
            <input value={block.registerSubheading ?? ''}
              onChange={(e) => upd({ registerSubheading: e.target.value || undefined })} style={INPUT} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Button label</label>
            <input value={block.registerSubmitLabel ?? ''} placeholder="Create account"
              onChange={(e) => upd({ registerSubmitLabel: e.target.value || undefined })} style={INPUT} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Name</label>
            <EnumSelect value={block.nameFields} fallback="none"
              options={[['none', 'Not asked'], ['single', 'Full name'], ['split', 'First + last name']]}
              onChange={(nameFields) => upd({ nameFields: nameFields === 'none' ? undefined : nameFields })} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Account identity</label>
            <EnumSelect value={block.registerIdentity} fallback="username"
              options={[['username', 'Username + email'], ['contact', 'Email and/or mobile number']]}
              onChange={(registerIdentity) => upd({ registerIdentity: registerIdentity === 'username' ? undefined : registerIdentity })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <div>
              <label style={FIELD_LABEL}>Calling code</label>
              <input value={block.phoneCountryCode ?? ''} placeholder="+91"
                onChange={(e) => upd({ phoneCountryCode: e.target.value.trim() || undefined })} style={INPUT} />
            </div>
            <div>
              <label style={FIELD_LABEL}>Mobile placeholder</label>
              <input value={block.phonePlaceholder ?? ''}
                onChange={(e) => upd({ phonePlaceholder: e.target.value || undefined })} style={INPUT} />
            </div>
          </div>
          <div>
            <label style={FIELD_LABEL}>Email placeholder</label>
            <input value={block.emailPlaceholder ?? ''}
              onChange={(e) => upd({ emailPlaceholder: e.target.value || undefined })} style={INPUT} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Password hint</label>
            <input value={block.passwordHint ?? ''} placeholder="At least 8 characters."
              onChange={(e) => upd({ passwordHint: e.target.value || undefined })} style={INPUT} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <div>
              <label style={FIELD_LABEL}>Terms link</label>
              <input value={block.termsHref ?? ''} placeholder="/terms"
                onChange={(e) => upd({ termsHref: e.target.value.trim() || undefined })} style={{ ...INPUT, fontFamily: 'monospace' }} />
            </div>
            <div>
              <label style={FIELD_LABEL}>Privacy link</label>
              <input value={block.privacyHref ?? ''} placeholder="/privacy"
                onChange={(e) => upd({ privacyHref: e.target.value.trim() || undefined })} style={{ ...INPUT, fontFamily: 'monospace' }} />
            </div>
          </div>
          <p style={{ ...HINT, margin: 0 }}>Either link adds a required "I agree" box.</p>
          <label style={FIELD_LABEL}>Note under the button</label>
          <input value={block.registerNote?.title ?? ''} placeholder="Title"
            onChange={(e) => upd({ registerNote: { ...(block.registerNote || {}), title: e.target.value || undefined } })} style={INPUT} />
          <input value={block.registerNote?.body ?? ''} placeholder="Text"
            onChange={(e) => upd({ registerNote: { ...(block.registerNote || {}), body: e.target.value || undefined } })} style={INPUT} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <input value={block.registerNote?.href ?? ''} placeholder="Link, e.g. /partner"
              onChange={(e) => upd({ registerNote: { ...(block.registerNote || {}), href: e.target.value.trim() || undefined } })} style={{ ...INPUT, fontFamily: 'monospace' }} />
            <input value={block.registerNote?.icon ?? ''} placeholder="Icon, e.g. building"
              onChange={(e) => upd({ registerNote: { ...(block.registerNote || {}), icon: e.target.value.trim() || undefined } })} style={INPUT} />
          </div>
        </div>
      )}
    </>
  )
}
